"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.convert = exports.convertToUnitType = exports.UnsupportedConversionError = exports.DensityRequiredError = exports.UnitTypeClassification = void 0;
exports.poundsToKilograms = poundsToKilograms;
exports.kilogramsToPounds = kilogramsToPounds;
exports.convertFromUnitsToUnits = convertFromUnitsToUnits;
exports.canConvertWithoutDensity = canConvertWithoutDensity;
exports.hasAcceptableUnit = hasAcceptableUnit;
exports.convertSpecificGravityToPoundsPerGallonDensity = convertSpecificGravityToPoundsPerGallonDensity;
exports.convertStringDensityUnitToEnumValue = convertStringDensityUnitToEnumValue;
exports.convertStringUnitToEnumValue = convertStringUnitToEnumValue;
exports.convertFacilityProductAmountsToUnit = convertFacilityProductAmountsToUnit;
const decimal_js_1 = __importDefault(require("decimal.js"));
const browser_1 = require("encamp-prisma/generated/browser");
const mathjs_1 = require("mathjs");
const storageAmounts_1 = require("../constants/storageAmounts");
const hasValue_1 = require("../utils/hasValue");
const errors_1 = require("./errors");
// This is to match the precision of the postgres `double precision` type
const DECIMAL_PRECISION = 15;
exports.UnitTypeClassification = {
    [browser_1.UnitType.POUNDS]: "Mass",
    [browser_1.UnitType.GALLONS]: "Volume",
    [browser_1.UnitType.CUBIC_FEET]: "Volume",
    [browser_1.UnitType.TONS]: "Mass",
    [browser_1.UnitType.KILOGRAMS]: "Mass",
    [browser_1.UnitType.METRIC_TONS]: "Mass",
    [browser_1.UnitType.LITERS]: "Volume",
    [browser_1.UnitType.BARRELS]: "Volume",
};
class DensityRequiredError extends Error {
    constructor() {
        super("Conversions between mass and volume require a density.");
        this.name = "DensityRequiredError";
    }
}
exports.DensityRequiredError = DensityRequiredError;
class UnsupportedConversionError extends Error {
    constructor(fromUnits, toUnits) {
        super(`Conversions from ${fromUnits} to ${toUnits} are currently not supported.`);
        this.name = "UnsupportedConversionError";
    }
}
exports.UnsupportedConversionError = UnsupportedConversionError;
(0, mathjs_1.createUnit)(`${storageAmounts_1.TIER_II_UNIT.POUNDS}`, "1 lb");
/**
 * Note: This is not for 55 gallon drums. It is a reference to the oil industry
 * standard of 42 gallons.
 */
(0, mathjs_1.createUnit)(`${storageAmounts_1.TIER_II_UNIT.BARRELS}`, "42 gallon");
/**
 * Convert pounds → kilograms. Thin wrapper around `convertFromUnitsToUnits` so
 * call sites can avoid hand-rolling the 0.45359237 conversion factor.
 */
function poundsToKilograms(pounds) {
    return convertFromUnitsToUnits(pounds, storageAmounts_1.TIER_II_UNIT.POUNDS, storageAmounts_1.TIER_II_UNIT.KILOGRAMS);
}
/**
 * Convert kilograms → pounds. Thin wrapper around `convertFromUnitsToUnits` so
 * call sites can avoid hand-rolling the 2.20462 conversion factor.
 */
function kilogramsToPounds(kilograms) {
    return convertFromUnitsToUnits(kilograms, storageAmounts_1.TIER_II_UNIT.KILOGRAMS, storageAmounts_1.TIER_II_UNIT.POUNDS);
}
function convertFromUnitsToUnits(amount, fromUnits, toUnits, density, densityUnits) {
    const fromBaseUnit = (0, mathjs_1.unit)(mathSafeUnit(fromUnits));
    const toBaseUnit = (0, mathjs_1.unit)(mathSafeUnit(toUnits));
    // if converting from similar types of units (mass to mass, volume to volume), just do the conversion
    if (fromBaseUnit.equalBase(toBaseUnit)) {
        return (0, mathjs_1.unit)(amount, mathSafeUnit(fromUnits)).to(mathSafeUnit(toUnits)).toNumber();
    }
    else if (isConvertBetweenMassAndVolume(fromBaseUnit, toBaseUnit)) {
        if (!density || !densityUnits) {
            throw new DensityRequiredError();
        }
        let [numerator, denominator] = densityUnits.split("/");
        if (
        // If the density needs to be inverted...
        (0, mathjs_1.unit)(mathSafeUnit(numerator)).units[0].unit.base.key !==
            toBaseUnit.units[0].unit.base.key) {
            [denominator, numerator] = densityUnits.split("/");
            density = Number(decimal_js_1.default.div(1, density).toPrecision(DECIMAL_PRECISION));
        }
        const densityWithCorrectNumerator = (0, mathjs_1.unit)(density, mathSafeUnit(numerator))
            .to(mathSafeUnit(toUnits))
            .toNumber();
        // mathjs doesn't support inverse units and that's why there are a couple of ugly inverses here.
        const unitConversionFactor = decimal_js_1.default.div(1, (0, mathjs_1.unit)(1, mathSafeUnit(denominator)).to(mathSafeUnit(fromUnits)).toNumber());
        return Number(decimal_js_1.default.mul(decimal_js_1.default.mul(amount, densityWithCorrectNumerator), unitConversionFactor).toPrecision(DECIMAL_PRECISION));
    }
    else {
        throw new UnsupportedConversionError(fromUnits, toUnits);
    }
}
function canConvertWithoutDensity(fromUnit, toUnit) {
    // We need density for barrels
    if (fromUnit === browser_1.UnitType.BARRELS && toUnit !== browser_1.UnitType.BARRELS) {
        return false;
    }
    const fromTierIIUnit = convertUnitEnum(fromUnit);
    const toTierIIUnit = convertUnitEnum(toUnit);
    if (!fromTierIIUnit || !toTierIIUnit) {
        return false;
    }
    const baseFromUnit = (0, mathjs_1.unit)(mathSafeUnit(fromTierIIUnit));
    const baseToUnit = (0, mathjs_1.unit)(mathSafeUnit(toTierIIUnit));
    const areSameUnit = baseFromUnit.equalBase(baseToUnit);
    const isInvalidConvert = isConvertBetweenMassAndVolume(baseFromUnit, baseToUnit);
    return areSameUnit || !isInvalidConvert;
}
/**
 * Determines if a unit is acceptable given a list of allowed units and whether density is available.
 * If density is available, any unit is acceptable (can be converted).
 * Otherwise, checks if the unit can be converted to any of the allowed units without density.
 * @param unit - The unit to check
 * @param allowedUnits - Array of allowed units
 * @param hasDensity - Whether density information is available
 * @returns true if the unit is acceptable, false otherwise
 */
function hasAcceptableUnit(unit, allowedUnits, hasDensity) {
    if (hasDensity) {
        return true;
    }
    return allowedUnits.some((au) => canConvertWithoutDensity(unit, au));
}
const SPECIFIC_GRAVITY_OF_WATER = 8.34;
function convertSpecificGravityToPoundsPerGallonDensity(specificGravityLow, specificGravityHigh) {
    const specificGravity = specificGravityHigh && !isNaN(specificGravityHigh) ? specificGravityHigh : specificGravityLow;
    return specificGravity * SPECIFIC_GRAVITY_OF_WATER;
}
function convertStringDensityUnitToEnumValue(unit) {
    if (!unit) {
        return undefined;
    }
    const lowerUnit = unit.toLowerCase();
    if (Object.values(storageAmounts_1.TIER_II_DENSITY_UNIT).includes(lowerUnit)) {
        return lowerUnit;
    }
    return undefined;
}
function convertStringUnitToEnumValue(unit) {
    const lowerUnit = unit?.toLowerCase();
    if (Object.values(storageAmounts_1.TIER_II_UNIT).includes(lowerUnit)) {
        return lowerUnit;
    }
    return storageAmounts_1.TIER_II_UNIT.POUNDS;
}
function isConvertBetweenMassAndVolume(fromUnit, toUnit) {
    // math.js will break up 'compound' units into their individual units. mass & volume
    // are not compound measurement types, so we should ensure that there are only 1 base unit
    // in each of the incoming units
    if (fromUnit.units.length !== 1 || toUnit.units.length !== 1) {
        return false;
    }
    else if (
    // unit.base.key is the category of unit represented (length, mass, volume, ect). We
    // want to make sure that both of the units are either MASS or VOLUME
    ![fromUnit.units[0].unit.base.key, toUnit.units[0].unit.base.key].every((x) => ["VOLUME", "MASS"].includes(x))) {
        return false;
    }
    return true;
}
/* Our units don't coincide with the mathjs library's units & they don't support spaces in a unit
 * so we need to do this string munging. Ideally we could default to their units but this is fine.
 */
function mathSafeUnit(value) {
    return value
        .replace("cubic feet", "cuft")
        .replace("cubic foot", "cuft")
        .replace("metric tons", "tonne");
}
/** @deprecated Stop converting your amounts this late in the game */
function convertFacilityProductAmountsToUnit(facilityProduct, unit, state, shouldConvertStorageLocations = true) {
    /*
     * For now, we need to make sure our facility product is in a unit we recognize
     * because all of our facility products currently store units as a string ;-(
     */
    const fromUnit = facilityProduct.unit?.toLowerCase() || storageAmounts_1.TIER_II_UNIT.POUNDS;
    if (!Object.values(storageAmounts_1.TIER_II_UNIT).includes(fromUnit)) {
        throw new Error(`Unexpected from unit for conversion: ${fromUnit}`);
    }
    const { product } = facilityProduct;
    if (!product) {
        throw Error(`FacilityProduct has no product, cannot convert units. id: ${facilityProduct.id}`);
    }
    const facilityProductCopy = {
        ...facilityProduct,
        unit,
    };
    if (fromUnit !== unit) {
        facilityProductCopy.maxAmount = convertFromUnitsToUnits(facilityProductCopy.maxAmount ?? 0, fromUnit, unit, product.density, product.densityUnits);
        facilityProductCopy.averageAmount = convertFromUnitsToUnits(facilityProductCopy.averageAmount ?? 0, fromUnit, unit, product.density, product.densityUnits);
        if (facilityProductCopy.maxAmountLargestContainer !== null &&
            facilityProductCopy.maxAmountLargestContainer !== undefined) {
            facilityProductCopy.maxAmountLargestContainer = convertFromUnitsToUnits(facilityProductCopy.maxAmountLargestContainer, fromUnit, unit, product.density, product.densityUnits);
        }
        if (unit === storageAmounts_1.TIER_II_UNIT.POUNDS && state) {
            const { averageAmount, maxAmount } = facilityProductCopy;
            facilityProductCopy.averageAmountCode = parseInt((0, storageAmounts_1.getAmountCode)(averageAmount.toString(), state));
            facilityProductCopy.maxAmountCode = parseInt((0, storageAmounts_1.getAmountCode)(maxAmount.toString(), state));
        }
    }
    if (shouldConvertStorageLocations && facilityProductCopy.locations) {
        facilityProductCopy.locations = facilityProductCopy.locations
            .filter(hasValue_1.hasValue)
            .map((location) => ({
            ...location,
            amount: location.amount
                ? convertFromUnitsToUnits(location.amount, fromUnit, unit, product.density, product.densityUnits)
                : location.amount,
            unit: unit,
        }));
    }
    return facilityProductCopy;
}
const convertToUnitType = (unit) => {
    switch (unit) {
        case storageAmounts_1.TIER_II_UNIT.POUNDS:
            return browser_1.UnitType.POUNDS;
        case storageAmounts_1.TIER_II_UNIT.BARRELS:
            return browser_1.UnitType.BARRELS;
        case storageAmounts_1.TIER_II_UNIT.CUBIC_FEET:
            return browser_1.UnitType.CUBIC_FEET;
        case storageAmounts_1.TIER_II_UNIT.TONS:
            return browser_1.UnitType.TONS;
        case storageAmounts_1.TIER_II_UNIT.METRIC_TONS:
            return browser_1.UnitType.METRIC_TONS;
        case storageAmounts_1.TIER_II_UNIT.LITERS:
            return browser_1.UnitType.LITERS;
        case storageAmounts_1.TIER_II_UNIT.GALLONS:
            return browser_1.UnitType.GALLONS;
        case storageAmounts_1.TIER_II_UNIT.KILOGRAMS:
            return browser_1.UnitType.KILOGRAMS;
    }
};
exports.convertToUnitType = convertToUnitType;
/**
 * This function converts the unit from the UnitType enum to the TIER_II_UNIT enum.
 * @param {UnitType | null | undefined} unit - The unit to be converted.
 * @returns {TIER_II_UNIT | undefined} The converted unit.
 */
const convertUnitEnum = (unit) => {
    switch (unit) {
        case browser_1.UnitType.POUNDS:
            return storageAmounts_1.TIER_II_UNIT.POUNDS;
        case browser_1.UnitType.BARRELS:
            return storageAmounts_1.TIER_II_UNIT.BARRELS;
        case browser_1.UnitType.CUBIC_FEET:
            return storageAmounts_1.TIER_II_UNIT.CUBIC_FEET;
        case browser_1.UnitType.TONS:
            return storageAmounts_1.TIER_II_UNIT.TONS;
        case browser_1.UnitType.METRIC_TONS:
            return storageAmounts_1.TIER_II_UNIT.METRIC_TONS;
        case browser_1.UnitType.LITERS:
            return storageAmounts_1.TIER_II_UNIT.LITERS;
        case browser_1.UnitType.GALLONS:
            return storageAmounts_1.TIER_II_UNIT.GALLONS;
        case browser_1.UnitType.KILOGRAMS:
            return storageAmounts_1.TIER_II_UNIT.KILOGRAMS;
    }
};
/**
 * This function converts the density unit from the DensityUnits enum to the TIER_II_DENSITY_UNIT enum.
 * @param {DensityUnits | null | undefined} unit - The density unit to be converted.
 * @returns {DensityUnit | undefined} The converted density unit.
 */
const convertDensityUnitEnum = (unit) => {
    switch (unit) {
        case browser_1.DensityUnits.POUNDS_PER_CUBIC_FOOT:
            return storageAmounts_1.TIER_II_DENSITY_UNIT.POUNDS_PER_CUBIC_FOOT;
        case browser_1.DensityUnits.POUNDS_PER_GALLON:
            return storageAmounts_1.TIER_II_DENSITY_UNIT.POUNDS_PER_GALLON;
        case browser_1.DensityUnits.POUNDS_PER_LITER:
            return storageAmounts_1.TIER_II_DENSITY_UNIT.POUNDS_PER_LITERS;
        case browser_1.DensityUnits.POUNDS_PER_BARREL:
            return storageAmounts_1.TIER_II_DENSITY_UNIT.POUNDS_PER_BARRELS;
        case browser_1.DensityUnits.KILOGRAMS_PER_GALLON:
            return storageAmounts_1.TIER_II_DENSITY_UNIT.KILOGRAMS_PER_GALLON;
        case browser_1.DensityUnits.KILOGRAMS_PER_LITER:
            return storageAmounts_1.TIER_II_DENSITY_UNIT.KILOGRAMS_PER_LITERS;
        case browser_1.DensityUnits.KILOGRAMS_PER_CUBIC_FOOT:
            return storageAmounts_1.TIER_II_DENSITY_UNIT.KILOGRAMS_PER_CUBIC_FOOT;
        case browser_1.DensityUnits.KILOGRAMS_PER_BARREL:
            return storageAmounts_1.TIER_II_DENSITY_UNIT.KILOGRAMS_PER_BARRELS;
        case browser_1.DensityUnits.TONS_PER_GALLON:
            return storageAmounts_1.TIER_II_DENSITY_UNIT.TONS_PER_GALLON;
        case browser_1.DensityUnits.TONS_PER_LITER:
            return storageAmounts_1.TIER_II_DENSITY_UNIT.TONS_PER_LITERS;
        case browser_1.DensityUnits.TONS_PER_CUBIC_FOOT:
            return storageAmounts_1.TIER_II_DENSITY_UNIT.TONS_PER_CUBIC_FOOT;
        case browser_1.DensityUnits.TONS_PER_BARREL:
            return storageAmounts_1.TIER_II_DENSITY_UNIT.TONS_PER_BARRELS;
        case browser_1.DensityUnits.METRIC_TONS_PER_GALLON:
            return storageAmounts_1.TIER_II_DENSITY_UNIT.METRIC_TONS_PER_GALLON;
        case browser_1.DensityUnits.METRIC_TONS_PER_LITER:
            return storageAmounts_1.TIER_II_DENSITY_UNIT.METRIC_TONS_PER_LITERS;
        case browser_1.DensityUnits.METRIC_TONS_PER_CUBIC_FOOT:
            return storageAmounts_1.TIER_II_DENSITY_UNIT.METRIC_TONS_PER_CUBIC_FOOT;
        case browser_1.DensityUnits.METRIC_TONS_PER_BARREL:
            return storageAmounts_1.TIER_II_DENSITY_UNIT.METRIC_TONS_PER_BARRELS;
        default:
            return undefined;
    }
};
/**
 * This function converts the amount from one unit to another.
 * It throws an error if the fromUnit or toUnit is not provided.
 * @param {number | null | undefined} amount - The amount to be converted.
 * @param {UnitType | null | undefined} from - The unit from which the amount is to be converted.
 * @param {UnitType} to - The unit to which the amount is to be converted.
 * @param {number | null | undefined} density - The density of the substance.
 * @param {DensityUnits | null | undefined} densityUnit - The unit of the density.
 * @returns {number | null} The converted amount.
 */
const convert = (amount, from, to, density, densityUnit) => {
    const fromUnit = convertUnitEnum(from);
    const toUnit = convertUnitEnum(to);
    if (!fromUnit) {
        throw new errors_1.CouldNotDetermineUnit("from");
    }
    if (!toUnit) {
        throw new errors_1.CouldNotDetermineUnit("to");
    }
    // short circuit if we don't need to convert
    if (fromUnit === toUnit || !amount)
        return amount ?? null;
    const densityUnits = convertDensityUnitEnum(densityUnit);
    try {
        const convertedAmount = convertFromUnitsToUnits(amount, fromUnit, toUnit, density, densityUnits);
        return convertedAmount;
    }
    catch (error) {
        if (error instanceof DensityRequiredError) {
            throw new errors_1.MissingDensity(fromUnit, toUnit);
        }
        if (error instanceof UnsupportedConversionError) {
            throw new errors_1.UnsupportedUnitConversion(fromUnit, toUnit);
        }
        throw error;
    }
};
exports.convert = convert;
//# sourceMappingURL=unitConversions.js.map